import { useEffect, useState } from 'react';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useClientTranslation } from '@/lib/core/i18n';

const MAX_LENGTH = 2000;

interface GiftMessageSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialMessage?: string;
  onSave: (message: string) => void;
  /** CMS label overrides (shopShoppingBagApi module) — i18n keys are the fallback. */
  title?: string;
  inputLabel?: string;
  saveLabel?: string;
  cancelLabel?: string;
}

/**
 * Personalised gift message editor. Uses a controlled textarea with an
 * inline character counter (max 2000). Save is disabled until the message
 * has non-whitespace content.
 */
export function GiftMessageSheet({
  open,
  onOpenChange,
  initialMessage = '',
  onSave,
  title,
  inputLabel,
  saveLabel,
  cancelLabel,
}: GiftMessageSheetProps) {
  const t = useClientTranslation();
  const [message, setMessage] = useState(initialMessage);

  useEffect(() => {
    if (open) setMessage(initialMessage);
  }, [open, initialMessage]);

  const canSubmit = message.trim().length > 0;

  return (
    <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle>
      <DrawerContent className="max-h-[90vh]">
        <div data-base-ui-swipe-ignore className="flex flex-1 flex-col gap-6 overflow-y-auto pt-4">
          <h2 className="font-pp-neue-montreal text-jncquoi-neutral-1000 px-4 text-center text-sm leading-3.5 font-normal tracking-wide uppercase">
            {title || t('App.Shop.AddYourGiftMessage')}
          </h2>

          <div className="flex flex-col gap-2 px-4">
            <label
              htmlFor="gift-message"
              className="font-inter text-jncquoi-neutral-1000 text-sm leading-none font-medium tracking-wide"
            >
              {inputLabel || t('App.Shop.PersonalizeMessage')}
            </label>
            <Textarea
              id="gift-message"
              value={message}
              onChange={event => setMessage(event.target.value.slice(0, MAX_LENGTH))}
              maxLength={MAX_LENGTH}
              placeholder={t('App.Shop.WriteMessagePlaceholder')}
              className="min-h-32 border-none tracking-wide"
            />
            <span
              aria-live="polite"
              className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[12px] leading-none tracking-[0.25px]"
            >
              {message.length}/{MAX_LENGTH}
            </span>
          </div>

          <div className="border-jncquoi-neutral-400 flex flex-col gap-3 border-t-[0.5px] px-4 pt-6 pb-10.5">
            <Button
              variant="primary-solid"
              palette="dark"
              className="w-full"
              disabled={!canSubmit}
              onClick={() => {
                onSave(message.trim());
                onOpenChange(false);
              }}
            >
              {saveLabel || t('App.Shop.AddMessage')}
            </Button>
            <Button
              variant="tertiary-link"
              palette="dark"
              className="w-full"
              onClick={() => onOpenChange(false)}
            >
              {cancelLabel || t('App.Shop.CancelSheet')}
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
